/**
 * dd-klaviyo-review-core.js
 *
 * Single source of truth for posting reviews to Klaviyo's public
 * `client/reviews` endpoint. Consumed by both `dd-reviews.js` (PDP section)
 * and `dd-review-nudge.js` (sitewide floating nudge) so the JSON:API body
 * shape, revision header, and 202/4xx/429 handling stay in one place.
 *
 * Exposes a namespaced global `window.DDKlaviyoReview` (no ES modules, to
 * match the theme's non-bundled script loading). Load this file BEFORE any
 * consumer that needs `postKlaviyoReview`.
 *
 * Contract:
 *   postKlaviyoReview({
 *     companyId,        // 6-char Klaviyo public key (required)
 *     email,            // verified session email (required)
 *     author,           // display name (required)
 *     title,            // optional review title
 *     content,          // review body (required, >= 10 chars enforced by caller)
 *     rating,           // integer 1-5 (required)
 *     productId,        // Shopify numeric product id (required)
 *     orderId,          // optional Shopify numeric order id — mapped to the
 *                       // JSON:API `relationships.order.data` per Klaviyo
 *                       // reviews schema (better than stuffing in metadata).
 *     images,           // optional string[] of image URLs or base-64 data URIs;
 *                       // passed through to `attributes.images` per Klaviyo
 *                       // reviews schema. Use `filesToImages()` to convert
 *                       // browser File objects into a compressed data-uri list.
 *     incentiveType,    // optional FTC-disclosure enum, one of:
 *                       //   'coupon_or_discount' | 'employee_review'
 *                       //   | 'free_product' | 'loyalty_points' | 'other'
 *                       //   | 'paid_promotion' | 'sweepstakes_entry'
 *                       // Drop Dead's review nudge gives $25 store credit, so
 *                       // pass 'coupon_or_discount' from the nudge call-site.
 *   }) => Promise<{ ok: boolean, rateLimited?: boolean, error?: string, res: Response }>
 *
 * Note on `metadata`: the public `client/reviews` endpoint rejects any
 * attribute outside its documented schema (rating, title, content, author,
 * email, review_type, product, images, incentive_type). Any ad-hoc metadata
 * (client_ref for idempotency, customer_type for verified-badge flavoring,
 * analytics source) needs a different channel — e.g. a Klaviyo event/profile
 * property fired alongside the review, not on the review record itself.
 *
 * Note on internal-reviewer suppression (TRAP — read before debugging):
 * Klaviyo silently suppresses the `Submitted review` METRIC event when the
 * reviewer's email matches the brand's own domain (or the profile is
 * otherwise flagged as internal/staff/employee). The review RECORD is still
 * created (visible in Klaviyo Reviews dashboard), the POST still returns
 * 202, the theme's optimistic success state still renders — but no metric
 * fires, so any Klaviyo Flow / Zapier trigger that listens to
 * `Submitted review` will never run, and the downstream reward credit
 * (CreditsYard) never lands.
 *
 * This is intentional Klaviyo behavior — without it, internal QA would
 * auto-credit themselves on every test submission and pollute review
 * sentiment analytics. Treat it as a feature.
 *
 * Symptom signature when this is the cause:
 *   1. Review record present in Klaviyo Reviews dashboard, status=Published
 *   2. Reviewer's profile shows the review attached
 *   3. Profile → Metrics and insights tab shows `Submitted review = 0`
 *   4. Metric activity feed contains other recent customers' reviews but
 *      not this reviewer's
 *
 * Always test the reward flow with a non-brand-domain email
 * (e.g. yourname+ddtest@gmail.com). See docs/contracts/review-rewards.md
 * for the full pipeline contract and recovery procedure.
 *
 * Result semantics:
 *   { ok: true }                          — HTTP 202 (or any 2xx). Review accepted.
 *   { ok: false, rateLimited: true }      — HTTP 429. Callers should show retry + backoff.
 *   { ok: false, error: '<detail>' }      — HTTP 4xx/5xx. `error` is `errors[0].detail`
 *                                           when Klaviyo provides it, else `HTTP <status>`.
 *
 * Klaviyo's endpoint documents 202 Accepted on success with no body, so we
 * never read response JSON on the happy path.
 *
 * Shared helpers:
 *   filesToImages(fileList, opts?) => Promise<string[]>
 *     Convert a FileList / File[] into a list of base-64 JPEG data URIs. Used
 *     by any review UI that lets customers attach photos. The conversion
 *     happens entirely in the browser — the bytes never touch our server —
 *     and Klaviyo ingests the data URIs directly on the create endpoint.
 *
 *     opts.maxCount      default 4  — hard cap on number of images
 *     opts.maxSideLength default 2048 — longest edge after downscale
 *     opts.quality       default 0.82 — JPEG quality (0–1)
 *     opts.maxBytesRaw   default 15 * 1024 * 1024 — reject pre-encoded files
 *                                                   larger than this
 *     Throws Error on quota / unsupported mime / decode failure so the
 *     caller can surface a targeted error.
 *
 *   verifiedBadge(customerType) => { key, label, iconSVG }
 *     Normalize the raw Helium Customer Fields value (e.g. "Salon Owner",
 *     "salon_owner", "salon-owner") into the presentation tuple used by every
 *     review surface. Unknown/blank values fall back to 'stylist' because
 *     Drop Dead's pre-2026 accounts predate the professional-type field and
 *     every approved account is at minimum a licensed stylist. The returned
 *     iconSVG is a full `<svg>…</svg>` string sized by CSS (currentColor).
 */
(function () {
  'use strict';

  if (window.DDKlaviyoReview && window.DDKlaviyoReview._v >= 6) return;

  const KLAVIYO_REVISION = '2026-04-15';
  const KLAVIYO_CLIENT_REVIEWS_URL = 'https://a.klaviyo.com/client/reviews';

  const SUPPORTED_IMAGE_MIMES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

  const INCENTIVE_TYPES = [
    'coupon_or_discount',
    'employee_review',
    'free_product',
    'loyalty_points',
    'other',
    'paid_promotion',
    'sweepstakes_entry',
  ];

  async function parseKlaviyoError(res) {
    try {
      const body = await res.json();
      const detail = body && body.errors && body.errors[0] && body.errors[0].detail;
      return detail || `HTTP ${res.status}`;
    } catch (_) {
      return `HTTP ${res.status}`;
    }
  }

  /**
   * Resize one File to a JPEG data URI using <canvas>. We always emit JPEG
   * to keep the base-64 payload small (vs. PNG) and to normalize HEIC/HEIF
   * inputs that modern iPhones produce — Safari decodes them natively into
   * an <img>, and we re-encode as JPEG before sending to Klaviyo.
   */
  function fileToImageDataUri(file, maxSideLength, quality) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        try {
          const scale = Math.min(1, maxSideLength / Math.max(img.naturalWidth, img.naturalHeight));
          const w = Math.max(1, Math.round(img.naturalWidth * scale));
          const h = Math.max(1, Math.round(img.naturalHeight * scale));
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          const dataUri = canvas.toDataURL('image/jpeg', quality);
          URL.revokeObjectURL(url);
          resolve(dataUri);
        } catch (err) {
          URL.revokeObjectURL(url);
          reject(err);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Could not decode image'));
      };
      img.src = url;
    });
  }

  async function filesToImages(fileList, opts) {
    const options = opts || {};
    const maxCount = options.maxCount || 4;
    const maxSideLength = options.maxSideLength || 2048;
    const quality = options.quality || 0.82;
    const maxBytesRaw = options.maxBytesRaw || 15 * 1024 * 1024;

    const files = Array.from(fileList || []).slice(0, maxCount);
    if (!files.length) return [];

    const out = [];
    for (const file of files) {
      if (!file || !file.type) {
        throw new Error('Unsupported file');
      }
      if (!SUPPORTED_IMAGE_MIMES.includes(file.type)) {
        throw new Error(`Unsupported image type: ${file.type}. Use JPEG, PNG, WebP, or HEIC.`);
      }
      if (file.size > maxBytesRaw) {
        throw new Error(`Image is too large (${Math.round(file.size / 1024 / 1024)} MB). Keep each photo under ${Math.round(maxBytesRaw / 1024 / 1024)} MB.`);
      }
      const dataUri = await fileToImageDataUri(file, maxSideLength, quality);
      out.push(dataUri);
    }
    return out;
  }

  async function postKlaviyoReview({
    companyId,
    email,
    author,
    title,
    content,
    rating,
    productId,
    orderId,
    images,
    incentiveType,
  }) {
    if (!companyId) throw new Error('postKlaviyoReview: companyId is required');
    if (!email) throw new Error('postKlaviyoReview: email is required');
    if (!productId) throw new Error('postKlaviyoReview: productId is required');

    const attributes = {
      review_type: 'review',
      email,
      author,
      content,
      rating,
      product: {
        external_id: String(productId),
        integration_key: 'shopify',
      },
    };
    if (title) attributes.title = title;

    if (Array.isArray(images) && images.length) {
      attributes.images = images.filter((s) => typeof s === 'string' && s.length > 0);
    }

    if (incentiveType) {
      if (INCENTIVE_TYPES.indexOf(incentiveType) === -1) {
        throw new Error(`postKlaviyoReview: invalid incentiveType "${incentiveType}"`);
      }
      attributes.incentive_type = incentiveType;
    }

    const payload = { data: { type: 'review', attributes } };

    if (orderId) {
      payload.data.relationships = {
        order: {
          data: { type: 'order', id: String(orderId) },
        },
      };
    }

    const url = `${KLAVIYO_CLIENT_REVIEWS_URL}?company_id=${encodeURIComponent(companyId)}`;

    let res;
    try {
      res = await fetch(url, {
        method: 'POST',
        headers: {
          revision: KLAVIYO_REVISION,
          'Content-Type': 'application/vnd.api+json',
          Accept: 'application/vnd.api+json',
        },
        body: JSON.stringify(payload),
      });
    } catch (networkErr) {
      return { ok: false, error: 'network', res: null, networkError: networkErr };
    }

    if (res.status === 202 || (res.ok && res.status >= 200 && res.status < 300)) {
      return { ok: true, res };
    }
    if (res.status === 429) {
      return { ok: false, rateLimited: true, res };
    }
    const detail = await parseKlaviyoError(res);
    return { ok: false, error: detail, res };
  }

  /* ── Verified badge normalization ────────────────────────────
     Drop Dead stores a pro's professional type in a Helium Customer Fields
     metafield (namespace `app--960624--helium`, key configurable at the
     call-site). The raw value is author-entered by Helium/admin and has
     historically shipped with inconsistent casing / separators — so we
     normalize aggressively before mapping to a badge. */

  /* Single unified glyph across all three tiers — Lucide `badge-check`.
     The label carries the differentiation (Stylist / Salon Owner / Student),
     the icon signals "this account is verified" regardless of category. Keeps
     the chip visually consistent with other verified marks across the web
     (Lucide is the shadcn/ui default) and leaves room for per-type hover
     copy if we ever add tooltips. */
  const VERIFIED_BADGE_ICON =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"/><path d="m9 12 2 2 4-4"/></svg>';

  const VERIFIED_BADGE_ICONS = {
    stylist: VERIFIED_BADGE_ICON,
    salon_owner: VERIFIED_BADGE_ICON,
    student: VERIFIED_BADGE_ICON,
  };

  const VERIFIED_BADGE_LABELS = {
    stylist: 'Verified Stylist',
    salon_owner: 'Verified Salon Owner',
    student: 'Verified Student',
  };

  function verifiedBadge(customerType) {
    let key = 'stylist';
    if (customerType && typeof customerType === 'string') {
      const slug = customerType
        .trim()
        .toLowerCase()
        .replace(/[\s\-]+/g, '_');
      if (slug === 'salon_owner' || slug === 'owner' || slug === 'salon') {
        key = 'salon_owner';
      } else if (slug === 'student' || slug === 'cosmetology_student') {
        key = 'student';
      } else if (slug === 'stylist' || slug === 'hairstylist' || slug === 'hair_stylist') {
        key = 'stylist';
      }
    }
    return {
      key,
      label: VERIFIED_BADGE_LABELS[key],
      iconSVG: VERIFIED_BADGE_ICONS[key],
    };
  }

  /* ── Reviewed-items ledger ─────────────────────────────────────
     Klaviyo's client/reviews endpoint returns 202 for every well-formed
     POST without surfacing an ID, and Klaviyo's downstream reviews
     moderation dashboard dedupes silently on (email, product) when the
     same profile submits a second review. Product policy is one review
     per customer per order-item — repeat purchasers get a fresh slot on
     each new order, so we key the gate by `(productId, orderId)`. The PDP
     review section doesn't always know orderId at submit time, so calls
     without one are stored under `<productId>:` (empty order segment) and
     lookups by productId alone match any stored entry for that product.

     Stored in localStorage under `dd_reviews_submitted_v1` as a flat map:
       { "<productId>:<orderId>": { submittedAt, rating, source } }

     TTL: 90 days. Entries older than that are garbage-collected on read
     so a stale localStorage on a shared device doesn't permanently gate
     the review surface. Moderation typically lands within hours; 90 days
     is a generous buffer for the "submitted but moderator hasn't looked
     yet" window while keeping the dataset bounded. */
  const REVIEWED_STORAGE_KEY = 'dd_reviews_submitted_v1';
  const REVIEWED_TTL_MS = 90 * 24 * 60 * 60 * 1000;

  function _readReviewedLedger() {
    try {
      const raw = localStorage.getItem(REVIEWED_STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return {};
      // GC stale entries while we're here.
      const now = Date.now();
      let mutated = false;
      for (const k of Object.keys(parsed)) {
        const entry = parsed[k];
        if (!entry || !entry.submittedAt || (now - entry.submittedAt) > REVIEWED_TTL_MS) {
          delete parsed[k];
          mutated = true;
        }
      }
      if (mutated) {
        try { localStorage.setItem(REVIEWED_STORAGE_KEY, JSON.stringify(parsed)); } catch (_) { /* quota / private mode */ }
      }
      return parsed;
    } catch (_) {
      return {};
    }
  }

  function _writeReviewedLedger(ledger) {
    try {
      localStorage.setItem(REVIEWED_STORAGE_KEY, JSON.stringify(ledger));
    } catch (_) { /* quota / private mode — gate is best-effort */ }
  }

  function _reviewedKey(productId, orderId) {
    return `${String(productId || '')}:${String(orderId || '')}`;
  }

  function markReviewSubmitted({ productId, orderId, rating, source } = {}) {
    if (!productId) return;
    const ledger = _readReviewedLedger();
    ledger[_reviewedKey(productId, orderId)] = {
      submittedAt: Date.now(),
      rating: typeof rating === 'number' ? rating : null,
      source: source || 'unknown',
    };
    _writeReviewedLedger(ledger);
  }

  /* Returns true if a review has been submitted for the given scope.
     - `{ productId, orderId }` → exact match only (nudge path, where the
       review is tied to a specific purchase line item).
     - `{ productId }` alone → any stored entry for that product, across
       every order + the orderless PDP submit path. This is what the PDP
       section uses to gate the "Write a review" button. */
  function wasReviewSubmitted({ productId, orderId } = {}) {
    if (!productId) return false;
    const ledger = _readReviewedLedger();
    if (orderId) {
      return Boolean(ledger[_reviewedKey(productId, orderId)]);
    }
    const prefix = `${productId}:`;
    return Object.keys(ledger).some((k) => k.startsWith(prefix));
  }

  /**
   * Authenticated GET to the Klaviyo reviews read proxy. Sends
   * X-DD-Reviews-Token when the theme provides one via data-proxy-token.
   */
  function fetchReviewsProxy(url, proxyToken, init) {
    const headers = Object.assign({}, (init && init.headers) || {});
    let requestUrl = url;
    if (proxyToken) {
      headers['X-DD-Reviews-Token'] = proxyToken;
      // Generated Vercel canary URLs are protected before the function runs.
      // For preview-theme testing only, derive the registered bypass value
      // from the canary request token. The canonical production host never
      // receives this query parameter.
      try {
        const parsed = new URL(url, window.location.origin);
        if (/^dd-klaviyo-reviews-[a-z0-9]+-drop-dead\.vercel\.app$/i.test(parsed.hostname)) {
          const bypass = proxyToken.replace(/[^a-z0-9]/gi, '').slice(0, 32);
          if (bypass.length === 32) {
            parsed.searchParams.set('x-vercel-protection-bypass', bypass);
            requestUrl = parsed.toString();
          }
        }
      } catch (_err) {
        /* fetch() below reports malformed URLs through the existing caller path */
      }
    }
    return fetch(requestUrl, Object.assign({}, init || {}, { headers }));
  }

  window.DDKlaviyoReview = {
    _v: 6,
    KLAVIYO_REVISION,
    KLAVIYO_CLIENT_REVIEWS_URL,
    SUPPORTED_IMAGE_MIMES,
    INCENTIVE_TYPES,
    VERIFIED_BADGE_LABELS,
    VERIFIED_BADGE_ICONS,
    VERIFIED_BADGE_ICON,
    postKlaviyoReview,
    parseKlaviyoError,
    filesToImages,
    verifiedBadge,
    markReviewSubmitted,
    wasReviewSubmitted,
    fetchReviewsProxy,
  };
})();
